import { useSubscription } from '../components/subscription-v2/hooks/useSubscription'
import { formatCurrency } from "@/lib/utils"
import { TransactionList } from '../components/billing/transaction-list'
export default function Billing() {
  const { billing, transactions } = useSubscription()

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }
  
  return (
    <div className='px-0 2xl:px-44 pt-20 sm:pt-6 xl:pt-10'>
      <main className="flex-1 overflow-y-auto">
          <div className="space-y-0.5">
            <h2 className="text-xl font-bold">Billing &amp; Subscription</h2>
            <p className="text-muted-foreground text-sm">Manage your subscription and view billing history</p>
          </div>
          <div className="grid gap-6 md:grid-cols-2 mt-6">
            <div className="rounded-xl bg-white text-gray-500 transition-colors">
              <div className="flex flex-col space-y-1.5 p-6">
                <div className="text-lg font-medium leading-none  text-black flex items-center gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 26 26" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-credit-card h-5 w-5"><rect width="20" height="14" x="2" y="5" rx="2"></rect><line x1="2" x2="22" y1="10" y2="10"></line></svg>Current Subscription
                </div>
                <div className="text-xs text-gray-500">Your active subscription details</div>
              </div>
              <div className="p-6 pt-0 space-y-4">
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Plan</span>
                    <span className="font-medium text-black"> {billing?.subscription?.planName} </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Status</span>
                    <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-green-100 text-green-800">active</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Billing Cycle</span>
                    <span className="font-medium capitalize text-black">{billing?.subscription?.paymentFrequency}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Price</span>
                    <span className="font-medium text-black">
                    {formatCurrency(billing?.subscription?.price || 0, billing?.billing?.currency || 'usd')}/
                    {billing?.subscription?.paymentFrequency === 'monthly' ? 'mo' : 'yr'}
                    </span>
                  </div>
                </div>
                <div className="pt-4 border-t space-y-2">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-calendar h-4 w-4"><path d="M8 2v4"></path><path d="M16 2v4"></path><rect width="18" height="18" x="3" y="4" rx="2"></rect><path d="M3 10h18"></path></svg>
                    <span>Current period started on {formatDate(billing?.subscription?.startDate || '')}</span>
                  </div>
                  {(billing?.subscription?.endDate || billing?.subscription?.planName === 'spark') && <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-clock h-4 w-4"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
                    <span>Next billing date: {billing?.subscription?.endDate ? formatDate(billing?.subscription?.endDate) : '-'}</span>
                  </div>}
                </div>
                <a className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors bg-blue-500 text-primary-foreground hover:bg-primary-600 h-10 px-4 py-2 w-full mt-4" href="/subscription">
                Change Plan
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-arrow-right ml-2 h-4 w-4"><path d="M5 12h14"></path><path d="m12 5 7 7-7 7"></path></svg>
                </a>
              </div>
            </div>
            <div className="rounded-xl bg-white text-gray-500 transition-colors">
              <div className="flex flex-col space-y-1.5 p-6">
                <div className="text-lg font-medium leading-none  text-black flex items-center gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 26 26" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-credit-card h-5 w-5"><rect width="20" height="14" x="2" y="5" rx="2"></rect><line x1="2" x2="22" y1="10" y2="10"></line></svg>
                  Billing Summary
                </div>
                <div className="text-xs text-gray-500">Your billing and payment information</div>
              </div>
              <div className="p-6 pt-0 space-y-4">
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Total Amount Paid</span>
                    <span className="font-medium text-black">
                    {formatCurrency(billing?.billing?.totalPaid || 0, billing?.billing?.currency || 'usd')}
                    </span>
                  </div>
                  {billing?.billing?.lastPaymentDate && <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Last Payment</span>
                    <span className="font-medium text-black">
                    {billing?.billing?.lastPaymentDate ? formatDate(billing?.billing?.lastPaymentDate) : '-'}
                    </span>
                  </div>}
                </div>
                <div className="rounded-lg bg-muted p-4 mt-4">
                  <h4 className="text-sm font-medium mb-2">Need Help?</h4>
                  <p className="text-sm text-muted-foreground">If you have any questions about your billing or subscription, please contact our support team.</p>
                  <a className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&amp;_svg]:pointer-events-none [&amp;_svg]:size-4 [&amp;_svg]:shrink-0 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10 px-4 py-2 w-full mt-4" href="/support/dashboard">Contact Support</a>
                  </div>
              </div>
            </div>
          </div>

          {/* Add Transaction List */}
          <div className="mt-6">
            <TransactionList transactions={transactions} />
          </div>
      </main>
    </div>
  );
}